import React from 'react';
import { startCase } from 'lodash';
import moment from 'moment-timezone';

import CardLayout from '@magoosh/layouts/card';
import { SuccessBadge } from '@magoosh/lib/badge';

import ContentCard from '../../components/content_card';
import NoContent from '../../../components/no_content';
import SectionHeader from '../../components/section_header';
import TextAsParagraphs from '../../components/text_as_paragraphs';
import { EssayScoreReport, SpeakingScoreReport } from '../../../types';

import MagooshAI from '../../assets/magoosh_ai.svg';

interface Props {
  examName: string;
  requestType: 'essay' | 'speaking';
  scoreReport: EssayScoreReport | SpeakingScoreReport;
  title?: string;
}

export default (props: Props) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const rubricUrl = () => {
    if (props.examName === 'GRE') {
      return 'https://www.ets.org/gre/test-takers/general-test/prepare/content/analytical-writing/scoring.html';
    } else if (props.examName === 'TOEFL' && props.requestType === 'essay') {
      return 'https://www.ets.org/pdfs/toefl/writing-rubrics.pdf';
    } else if (props.examName === 'TOEFL' && props.requestType === 'speaking') {
      return 'https://www.ets.org/pdfs/toefl/speaking-rubrics.pdf';
    } else {
      return null;
    }
  };

  return (
    <CardLayout.Card className="d-flex flex-column flex-md-row gap-m gap-lg-xl">
      <SectionHeader
        subtitle={<>
          <div className="small">
            This evaluation is based on the {props.examName} scoring rubric.
          </div>

          {
            rubricUrl() && (
              <div style={{ lineHeight: 1 }}>
                <a
                  className="text-no-decoration text-secondary"
                  href={rubricUrl()}
                  style={{ fontSize: 12 }}
                  target="_blank"
                >
                  <strong>See the scoring rubric</strong>
                </a>
              </div>
            )
          }
        </>}
        supertitle="Feedback"
        supertitleIcon="commenting"
        title={props.title || 'Your score explained'}
      />

      <div className="d-flex flex-1 flex-column gap-m" style={{ minWidth: 0 }}>
        <ContentCard className="d-flex flex-1 flex-column gap-m">
          {
            props.scoreReport.status === 'finished' ? (
              <>
                <div className="align-items-center d-flex gap-s">
                  {
                    props.scoreReport.grader.type === 'ai' && (
                      <img alt="" src={MagooshAI} />
                    )
                  }

                  <div className="small">
                    {props.scoreReport.grader.name}
                    {' '} on {moment.unix(props.scoreReport.finishedAt).tz(timeZone).format('MMMM D, YYYY')}
                  </div>
                </div>

                <div className="d-flex flex-column flex-1" style={{ gap: 20 }}>
                  {
                    props.scoreReport.subscores && (
                      <div className="d-flex flex-row gap-xs flex-wrap">
                        {
                          Object.keys(props.scoreReport.subscores).sort().map((key) => (
                            <SuccessBadge key={key}>
                              <div className="d-flex gap-xs" style={{ fontSize: 13 }}>
                                <div>{startCase(key)}</div>
                                <strong>{props.scoreReport.subscores[key]}</strong>
                              </div>
                            </SuccessBadge>
                          ))
                        }
                      </div>
                    )
                  }

                  <div>
                    <TextAsParagraphs text={props.scoreReport.content.narrative} />
                  </div>

                  {
                    (props.scoreReport.content.narrativeSections || []).map((section, idx) => (
                      <div key={idx} className="d-flex flex-column" style={{ gap: 10 }}>
                        <div>
                          <strong>{section.title}</strong>
                        </div>

                        <div>
                          <ul>
                            {
                              section.bullets.map((bullet, bulletIdx) => (
                                <li key={bulletIdx}>{bullet}</li>
                              ))
                            }
                          </ul>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </>
            ) : (
              <NoContent />
            )
          }
        </ContentCard>
      </div>
    </CardLayout.Card>
  );
};
